<!--
  Edit a record over the page it is listed on, instead of routing away to
  `/item/edit` and back.

  It talks to the SAME read and update URLs the edit PAGE does, built the same
  way, and posts the same payload — BreadModelForm is the form in both places.
  Only where the form lives changes.

  Opened either way:
    · `open({ serviceKey, modelKey, modelId })` from a parent that has a ref
    · a `requestModelEdit` store action, for the row action buttons that are
      mounted imperatively by a column formatter and so have no parent to call

  A host mounting this registers itself with the store (see
  store/modules/modelEditDialog.js), which is what tells those buttons a dialog
  exists here at all; on a page without one they route as they always have.
-->
<template>
  <v-dialog
    v-model="isOpen"
    max-width="680"
    :persistent="apiUpdate.isLoading"
    content-class="med-dialog"
    @click:outside="close"
  >
    <div v-if="isOpen" class="med-modal">
      <div class="med-modal__head">
        <div class="med-modal__identity">
          <h2 class="med-modal__title">Edit {{ model.name.singular }}</h2>
          <p class="med-modal__subtitle">{{ recordTitle }}</p>
        </div>
        <button class="med-modal__close" title="Close" @click="close">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <div class="med-modal__body">
        <AError :api="apiRead" />

        <BreadModelForm
          ref="form"
          embedded
          action="edit"
          :isLoading="apiRead.isLoading"
          :api="apiUpdate"
          :model="model"
        />
      </div>

      <div class="med-modal__foot">
        <button
          class="med-modal__btn med-modal__btn--ghost"
          type="button"
          @click="close"
        >
          Cancel
        </button>
        <v-btn
          class="med-modal__btn med-modal__btn--primary"
          depressed
          :disabled="apiRead.isLoading"
          :loading="apiUpdate.isLoading"
          @click="submit"
        >
          Save changes
        </v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import Api from "@/objects/api";
import BreadModelForm from "@/components/common/BreadModelForm.vue";
import Model from "@/objects/model";
import Service from "@/objects/service";
import { mapState } from "vuex";

export default {
  name: "BreadModelEditDialog",
  components: {
    BreadModelForm,
  },
  computed: {
    ...mapState({
      editRequest: (state) => state.modelEditDialog.data.request,
    }),
    recordTitle() {
      if (!this.record) return `ID ${this.modelId}`;
      const textColumn = this.model.column && this.model.column.text;
      const title = textColumn ? this.record[textColumn] : this.record.name;
      return title || `ID ${this.modelId}`;
    },
  },
  data: () => ({
    apiRead: new Api(),
    apiUpdate: new Api(),
    model: new Model(),
    service: new Service(),
    modelId: null,
    record: null,
    isOpen: false,
  }),
  watch: {
    /**
     * The store request is a one-shot: it is cleared as soon as it is taken, so
     * editing the same row twice in a row still arrives as a change.
     */
    editRequest(request) {
      if (!request) return;
      this.$store.dispatch("clearModelEditRequest");
      this.open(request);
    },
  },
  created() {
    this.$store.dispatch("registerModelEditDialogHost");
  },
  beforeDestroy() {
    this.$store.dispatch("unregisterModelEditDialogHost");
  },
  methods: {
    /**
     * `modelKey` is read from the request rather than the route: a child table
     * lists a different model than the page it is on.
     */
    open({ modelKey, modelId }) {
      this.model.getByKey(modelKey);
      if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);
      this.modelId = modelId;
      this.record = null;

      // Not callbackReset(): despite the name that is Api's pre-flight hook and
      // sets isLoading TRUE, which through `:persistent` would open the dialog
      // spinning and refusing to close.
      this.apiUpdate.isError = false;
      this.apiUpdate.error = null;

      this.apiRead.setMethod("GET");
      this.apiRead.setUrl(this.readUrl());
      this.apiRead.setCallbackCompleted((resp) => {
        this.record = resp;
        // nextTick because the form only exists once the dialog has rendered,
        // and a cached response can land before that.
        this.$nextTick(() => {
          if (!this.$refs.form) return;
          // A clone, so typing edits the buffer and not the row behind the
          // dialog; Cancel then really discards.
          this.$refs.form.setData(this.$_.cloneDeep(resp));
        });
      });

      this.apiUpdate.setMethod("POST");
      this.apiUpdate.setUrl(this.updateUrl());
      this.apiUpdate.setCallbackCompleted(() => {
        this.isOpen = false;
        this.$emit("updated", { modelKey: this.model.key, modelId: this.modelId });
        this.$store.dispatch("showMessage", {
          message: "Updated successfully.",
          messageType: "success",
          timeout: 2000,
        });
      });

      this.isOpen = true;
      this.apiRead.fetch();
    },
    close() {
      if (this.apiUpdate.isLoading) return;
      this.isOpen = false;
    },
    submit() {
      if (this.$refs.form) this.$refs.form.validate();
    },
    readUrl() {
      if (this.model.apiUrls.apiReadUrl != "") {
        return `${this.$service[this.service.key]}${
          this.model.apiUrls.apiReadUrl
        }/${this.modelId}/read`;
      }
      return `${
        this.$service[this.service.key]
      }/v1/en/console/tabulator/model/${this.model.key}/${this.modelId}/read`;
    },
    updateUrl() {
      if (this.model.apiUrls.apiUpdateUrl != "") {
        return `${this.$service[this.service.key]}${
          this.model.apiUrls.apiUpdateUrl
        }/${this.modelId}/edit`;
      }
      return `${
        this.$service[this.service.key]
      }/v1/en/console/tabulator/model/${this.model.key}/${this.modelId}/edit`;
    },
  },
};
</script>

<style scoped>
/* Same three tiers as the read page's dialog: chrome on --app-card, the form
   area a step back on --app-surface, the field wells a step forward again. Every
   band is painted — a transparent one shows the overlay scrim through the
   panel. Token-only colour, so dark needs no companion block. */
.med-modal {
  background: var(--app-card);
  font-family: var(--tbl-font);
}

.med-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  background: var(--app-card);
  border-bottom: 1px solid var(--app-border);
}

.med-modal__identity {
  min-width: 0;
}

.med-modal__title {
  font-size: 0.98rem;
  font-weight: 700;
  color: var(--app-text);
  margin: 0;
}

.med-modal__subtitle {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--app-text-faint);
  margin: 3px 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.med-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--app-text-faint);
  transition: all 0.15s ease;
}

.med-modal__close:hover {
  background: var(--app-hover);
  color: var(--app-text);
}

/* Capped so a model with many fields scrolls the FORM rather than growing the
   dialog past the viewport and pushing its own Save button off-screen. */
.med-modal__body {
  padding: 22px 24px 26px;
  max-height: 60vh;
  overflow-y: auto;
  background: var(--app-surface);
}

.med-modal__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid var(--app-border);
  background: var(--app-card);
}

.med-modal__btn {
  height: 38px;
  padding: 0 20px;
  border-radius: 10px;
  font-family: var(--tbl-font);
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: normal;
  text-transform: none;
  transition: all 0.15s ease;
}

.med-modal__btn--ghost {
  border: 1px solid var(--app-border-strong);
  background: transparent;
  color: var(--app-text-muted);
}

.med-modal__btn--ghost:hover {
  background: var(--app-hover);
  color: var(--app-text);
}

.med-modal__btn--primary.v-btn {
  background: var(--app-accent) !important;
  color: var(--app-accent-fg) !important;
  box-shadow: none;
}

.med-modal__btn--primary.v-btn:hover {
  background: var(--app-accent-strong) !important;
}

@media (max-width: 599px) {
  .med-modal__head,
  .med-modal__foot {
    padding: 12px;
  }

  .med-modal__body {
    padding: 14px 12px 18px;
    max-height: 66vh;
  }

  .med-modal__btn {
    flex: 1;
  }
}
</style>

<style>
/* Unscoped: VDialog moves this content out of the component, and `content-class`
   lands on an element it owns rather than on our root, so a scoped selector
   never reaches either. */
.v-dialog.med-dialog {
  border-radius: 14px;
  background: var(--app-card);
  border: 1px solid var(--app-border);
  box-shadow: 0 24px 60px var(--app-shadow-deep, rgba(0, 0, 0, 0.35));
}

/* Vuetify paints every sheet its built-in near-black in the dark theme; the
   embedded form's card is meant to be a wrapper, not a surface. */
.v-dialog.med-dialog .v-card.bmf__card--embedded {
  background: transparent !important;
  box-shadow: none !important;
}
</style>
